import React, { useState, useEffect } from 'react';
import { useChat } from '../../Context/ChatContext';
import ChatView from './ChatView';
import ChatLauncher from './ChatLauncher';
import { FaTimes, FaCommentDots, FaArrowLeft } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../Firebase/firebase';
import { formatDistanceToNowStrict } from 'date-fns';
import { useWindowSize } from '../../hooks/useWindowSize';

const FloatingChatWindow = () => {
    const chatContext = useChat();
    const [activeUserPresence, setActiveUserPresence] = useState({ isOnline: false, lastSeen: null });
    const { width } = useWindowSize();
    const isMobile = width < 640;
    const bottomNavHeight = 56; // Height of the bottom navigation bar in mobile view

    const {
        chatState,
        activeChatUser,
        activeChatId,
        closeChatWindow,
        openChatLauncher,
        returnToLauncher,
        totalUnreadCount = 0,
    } = chatContext || {};

    useEffect(() => {
        let unsubscribe;
        if (chatState === 'chatting' && activeChatUser?.userId) {
            const userRef = doc(db, 'users', activeChatUser.userId);
            unsubscribe = onSnapshot(userRef, (docSnap) => {
                if (docSnap.exists()) {
                    setActiveUserPresence({
                        isOnline: docSnap.data().isOnline || false,
                        lastSeen: docSnap.data().lastSeen || null,
                    });
                }
            }, (error) => {
                console.error("Error listening to active user presence:", error);
                setActiveUserPresence({ isOnline: false, lastSeen: null });
            });
        } else {
            setActiveUserPresence({ isOnline: false, lastSeen: null });
        }

        return () => unsubscribe?.();
    }, [chatState, activeChatUser?.userId]);

    if (!chatContext) return null;

    const ToggleButton = () => {
        const isPanelOpen = chatState === 'launcher' || chatState === 'chatting';
        const clickHandler = isPanelOpen ? closeChatWindow : openChatLauncher;
        const label = isPanelOpen ? "Close Chat" : "Open Chat";
        const Icon = isPanelOpen ? FaTimes : FaCommentDots;
        const showBadge = chatState === 'closed' && totalUnreadCount > 0;

        return (
            <AnimatePresence>
                <motion.button
                    key="chat-toggle-button-dynamic"
                    initial={{ scale: 0, y: 50 }}
                    animate={{ scale: 1, y: 0 }}
                    transition={{ type: 'spring', stiffness: 150, damping: 15 }}
                    onClick={clickHandler}
                    className={`fixed ${isMobile ? 'right-4' : 'right-5'} bg-indigo-600 dark:bg-indigo-500 text-white p-4 rounded-full shadow-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 z-[60] flex items-center justify-center`}
                    aria-label={label}
                    style={{
                        bottom: isMobile ? `${bottomNavHeight + 16}px` : '20px'
                    }}
                >
                    <Icon size={24} />
                    <AnimatePresence>
                        {showBadge && (
                            <motion.span
                                key="notification-badge"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0 }}
                                transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
                                className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center border-2 border-white dark:border-gray-800"
                                aria-live="polite"
                            >
                                {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
                            </motion.span>
                        )}
                    </AnimatePresence>
                </motion.button>
            </AnimatePresence>
        );
    };

    const showChatPanel = chatState === 'launcher' || chatState === 'chatting';
    const showBackButton = chatState === 'chatting';

    const formatLastSeen = (timestamp) => {
        if (!timestamp?.toDate) return 'unknown';
        try {
            return formatDistanceToNowStrict(timestamp.toDate(), { addSuffix: true });
        } catch (e) {
            return 'unknown';
        }
    };

    let headerTitle = 'Start a Conversation';
    let headerSubtitle = null;

    if (chatState === 'chatting' && activeChatUser) {
        headerTitle = activeChatUser.name || 'Chat';
        if (activeUserPresence.isOnline) {
            headerSubtitle = <span className="text-xs text-green-600">Online</span>;
        } else if (activeUserPresence.lastSeen) {
            headerSubtitle = <span className="text-xs text-gray-500 dark:text-gray-400">Last seen {formatLastSeen(activeUserPresence.lastSeen)}</span>;
        } else {
            headerSubtitle = <span className="text-xs text-gray-500 dark:text-gray-400">Offline</span>;
        }
    }

    return (
        <>
            {chatState === 'closed' && <ToggleButton />}

            <AnimatePresence>
                {showChatPanel && (
                    <motion.div
                        key="chat-panel"
                        initial={{ y: "100%", opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "100%", opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                        className={`fixed ${isMobile ? 'right-0 left-0' : 'right-5 md:right-10 lg:right-20 w-80 md:w-96'} w-full bg-white dark:bg-gray-900 rounded-t-lg shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col z-50`}
                        style={{
                            height: isMobile ? `calc(70% - ${bottomNavHeight}px)` : '500px',
                            bottom: isMobile ? `${bottomNavHeight}px` : '0',
                            maxHeight: isMobile ? 'none' : '500px',
                            zIndex: 400
                        }}
                        aria-modal="true"
                        role="dialog"
                        aria-labelledby="chat-window-title"
                    >
                        <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-t-lg flex-shrink-0">
                            <div className="flex items-center space-x-2 flex-grow min-w-0">
                                {showBackButton && (
                                    <button
                                        onClick={returnToLauncher}
                                        className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white p-1 -ml-1"
                                        aria-label="Back to chat list"
                                    >
                                        <FaArrowLeft />
                                    </button>
                                )}
                                {chatState === 'chatting' && activeChatUser && (
                                    <div className="relative flex-shrink-0">
                                        <img
                                            src={activeChatUser.profilePicUrl || '/default-profile.jpg'}
                                            alt={activeChatUser.name || 'User'}
                                            className="w-8 h-8 rounded-full object-cover"
                                        />
                                        {activeUserPresence.isOnline && (
                                            <span className="absolute bottom-0 right-0 block h-2 w-2 rounded-full bg-green-500 ring-1 ring-white dark:ring-gray-900"></span>
                                        )}
                                    </div>
                                )}
                                <div className="flex flex-col min-w-0">
                                    <h2
                                        id="chat-window-title"
                                        className="font-semibold text-gray-800 dark:text-white text-sm truncate"
                                    >
                                        {headerTitle}
                                    </h2>
                                    {headerSubtitle && (
                                        <div className="leading-tight -mt-0.5">
                                            {headerSubtitle}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={closeChatWindow}
                                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white flex-shrink-0 ml-2"
                                aria-label="Close chat window"
                            >
                                <FaTimes />
                            </button>
                        </div>
    
                        <div className="flex-grow overflow-hidden dark:bg-gray-900">
                            {chatState === 'launcher' ? (
                                <ChatLauncher />
                            ) : chatState === 'chatting' && activeChatUser && activeChatId ? (
                                <ChatView
                                    key={activeChatId}
                                    chatId={activeChatId}
                                    receiverUserId={activeChatUser.userId}
                                    receiverName={activeChatUser.name}
                                    receiverPic={activeChatUser.profilePicUrl}
                                    isMobile={isMobile}
                                />
                            ) : (
                                <div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading chat...</div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

export default FloatingChatWindow;